import { type ReactElement, useMemo } from 'react'
import type { TraceStep } from '../types'

export interface NegativeEvidenceProps {
  steps: TraceStep[]
  /** Wired to the page's "select step in timeline" handler so users
   *  can jump from a summary entry back to the originating step's
   *  detail panel. */
  onSelectStep?: (stepId: string) => void
}

interface Finding {
  step_id: string
  tool_name: string
  /** Short, human-readable summary like "0 matches for PIC-1226 in
   *  packages/equipment/tag-index.ts". */
  summary: string
  kind: 'error' | 'no_matches' | 'not_found'
}

/**
 * "What the agent tried but couldn't find" panel — top-of-page summary
 * (AGNT-685 PR I) of failed / empty tool calls so engineers debugging
 * an "I cannot determine" answer don't have to spelunk the timeline
 * to find where the evidence ran out.
 *
 * Heuristics, applied per step:
 *   - status === 'error'     → kind=error,   summary = tool: errorMessage
 *   - empty grep / find_*    → kind=no_matches with searched scope
 *   - ENOENT in errorMessage → kind=not_found with the requested path
 *
 * Renders nothing when no findings — the panel disappears once the
 * trace has only successful tool calls.
 */
export function NegativeEvidence({
  steps,
  onSelectStep,
}: NegativeEvidenceProps): ReactElement | null {
  const findings = useMemo(() => deriveFindings(steps), [steps])
  if (findings.length === 0) return null
  return (
    <section
      // Padding lives on this element (not the parent wrapper) so a
      // fully-successful trace where this component returns null
      // doesn't leave a 12px gap between TraceHeader and the timeline.
      // Greptile / Copilot #11558.
      className="border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 rounded p-3 mx-6 mt-3 flex flex-col gap-2"
      data-testid="negative-evidence"
    >
      <header className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
        What the agent tried but couldn&apos;t find ({findings.length})
      </header>
      <ul className="flex flex-col gap-1 text-xs">
        {findings.map((f) => (
          <li key={f.step_id}>
            <button
              type="button"
              onClick={() => onSelectStep?.(f.step_id)}
              className="w-full text-left hover:underline text-amber-900 dark:text-amber-200 font-mono"
              data-testid={`negative-evidence-${f.kind}-${f.step_id}`}
            >
              <span className="text-amber-700 dark:text-amber-400 mr-2">[{labelForKind(f.kind)}]</span>
              <span className="text-foreground">{f.tool_name}</span>
              <span className="text-amber-700 dark:text-amber-400 mx-1">—</span>
              <span>{f.summary}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function labelForKind(kind: Finding['kind']): string {
  switch (kind) {
    case 'error':
      return 'error'
    case 'no_matches':
      return '0 hits'
    case 'not_found':
      return 'not found'
  }
}

/**
 * Pure helper for derivation — exported for unit testing without
 * having to mount the component.
 */
export function deriveFindings(steps: TraceStep[]): Finding[] {
  const findings: Finding[] = []
  for (const step of steps) {
    // Errors take precedence: a step that failed is more interesting
    // than a step that succeeded with no results, even for the same
    // tool. Sub-classify ENOENT-shaped errors as `not_found` so the
    // panel renders the readable "not found" label.
    if (step.status === 'error') {
      const err = step.error ?? '(no error message)'
      const isEnoent = /ENOENT|no such file/i.test(err)
      findings.push({
        step_id: step.step_id,
        tool_name: step.tool_name,
        summary: summarizeError(err),
        kind: isEnoent ? 'not_found' : 'error',
      })
      continue
    }
    if (isEmptyResult(step)) {
      findings.push({
        step_id: step.step_id,
        tool_name: step.tool_name,
        summary: summarizeEmptyResult(step),
        kind: 'no_matches',
      })
    }
  }
  return findings
}

function isEmptyResult(step: TraceStep): boolean {
  if (step.outputs == null) return false
  // Ignore non-tool steps (generations, etc.).
  if (!step.tool_name.startsWith('tool.')) return false
  const text =
    typeof step.outputs === 'string'
      ? step.outputs
      : JSON.stringify(step.outputs)
  // Anchored to the start of the trimmed output — tools that returned
  // empty results emit one of these sentinels as the FIRST line, not
  // buried inside a long markdown body. Matching only at the head
  // avoids false positives like "3 distinct results (0 results from
  // secondary index)" where the agent did surface real findings.
  // Greptile P2 #11558. (No trailing `\b` — `)` is non-word so the
  // boundary doesn't match at end-of-string for `(no matches)`.)
  const trimmed = text.trimStart()
  return /^(?:\(no matches\)|no matches found|0 results|\(empty\))/i.test(trimmed)
}

/**
 * Format a tool's error message for the summary panel. ENOENT errors
 * include the absolute path the agent tried to open; surface that
 * verbatim (the absolute path includes the workspace prefix, but
 * stripping it is brittle without knowing the per-tenant root and
 * "did not exist" is more important than a tidy display). Other
 * errors are truncated to 140 chars so a single multi-line stack
 * trace doesn't push other findings off-screen.
 */
function summarizeError(err: string): string {
  const enoent = /ENOENT[^']*'([^']+)'/.exec(err)
  if (enoent) {
    return `path ${enoent[1]} did not exist`
  }
  return err.length > 140 ? `${err.slice(0, 137)}…` : err
}

function summarizeEmptyResult(step: TraceStep): string {
  const inputs = step.inputs as Record<string, unknown> | null
  if (!inputs || typeof inputs !== 'object') return '0 matches'
  // Best-effort summary: pull `pattern` and `scope` for grep,
  // `query` for find_doc_sections, `tag` for trace_connections.
  const pattern = typeof inputs.pattern === 'string' ? inputs.pattern : null
  const scope = typeof inputs.scope === 'string' ? inputs.scope : null
  const path = typeof inputs.path === 'string' ? inputs.path : null
  const query = typeof inputs.query === 'string' ? inputs.query : null
  if (pattern && (scope || path)) {
    return `0 matches for "${pattern}" in ${scope ?? path}`
  }
  if (pattern) return `0 matches for "${pattern}"`
  if (query) return `0 results for "${query}"`
  return '0 matches'
}
